export interface AuthRedirectErrorParams {
  error: string | null;
  errorCode: string | null;
  description: string | null;
}

/**
 * Supabase reports an auth redirect failure — an OAuth provider rejection, or an email link (reset
 * password, magic link) that's already been used or has expired — by appending
 * `error`/`error_code`/`error_description` to whatever URL it sends the browser back to. Depending
 * on which flow failed and which redirect shape produced it, those land in the query string, the
 * hash, or both at once, so every caller has to check both rather than picking one.
 *
 * `description` has its `+`-for-space encoding undone; `error`/`error_code` are returned raw since
 * callers branch on them as tokens (`otp_expired`, `access_denied`), not prose.
 */
export function readAuthRedirectErrorParams(search: string, hash: string): AuthRedirectErrorParams {
  const params = new URLSearchParams(search);
  const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);

  const error = params.get('error') || hashParams.get('error');
  const errorCode = params.get('error_code') || hashParams.get('error_code');
  const rawDescription = params.get('error_description') || hashParams.get('error_description');

  return {
    error,
    errorCode,
    description: rawDescription ? rawDescription.replace(/\+/g, ' ') : null,
  };
}
